import { Component, OnInit } from '@angular/core';
import { AccountInfoService } from '../../services/account-info.service';
import { Router } from '@angular/router';
import { concat, Observable, of, Subject } from 'rxjs';
import { GeocodingService } from '../../services/geocoding.service';
import { catchError, distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ErrorModalComponent } from '../modals/error-modal/error-modal.component';
import moment from "moment";
import _ from 'lodash';
import { DataService } from 'src/app/services/data.service';
import { environment } from 'src/environments/environment';

export interface AccountInfo {
  firstName: string;
  gender: string;
  address: { street: string, houseNumber: string, postalCode: string, municipality: string, country: string; };
  acceptedTermsOfUse: boolean;
  acceptedPrivacyPolicy: boolean;
  sendToInboxFromPhone: boolean;
  language: string;
  userId: string;
  iam: any;
  birthYear: number;
}

@Component({
  selector: 'app-account-info',
  templateUrl: './account-info.component.html',
  styleUrls: ['./account-info.component.scss'],
})
export class AccountInfoComponent implements OnInit {
  public accountInfoPageRead = false;
  public accountInfoPageAgree = false;
  public saving = false;
  public countries = [];
  public birthYearRange = {
    min: this.getMinBirthYear(),
    max: this.getMaxBirthYear()
  };

  public model: AccountInfo = {
    firstName: "",
    address: {
      street: "", houseNumber: "", postalCode: "", municipality: "",
      country: "country_be",
    },
    gender: "",
    acceptedTermsOfUse: false,
    acceptedPrivacyPolicy: false,
    sendToInboxFromPhone: false,
    language: 'ENG',
    userId: null,
    iam: "",
    birthYear: null,
  };

  address$: Observable<any[]>;
  addressLoading: boolean = false;
  addressInput$ = new Subject<string>();

  public genders = [
    { value: 'MALE', id: 'Male', label: 'GENDER_OPTION_MALE' },
    { value: 'FEMALE', id: 'Female', label: 'GENDER_OPTION_FEMALE' },
    { value: 'TRANSGENDER', id: 'Transgender', label: 'GENDER_OPTION_TRANSGENDER' },
    { value: 'NON_BINARY', id: 'Non-Binary', label: 'GENDER_OPTION_NON_BINARY' },
    { value: 'AGENDER', id: 'Agender', label: 'GENDER_OPTION_AGENDER' },
    { value: 'OTHER', id: 'Other', label: 'GENDER_OPTION_OTHER' },
    { value: 'UNDISCLOSED', id: 'Undisclosed', label: 'GENDER_OPTION_UNDISCLOSED' },
  ];
  public iam = [
    //Pupil , teacher and parent, other
    { value: 'PUPIL', id: 'pupil', label: 'Pupil' },
    { value: 'TEACHER', id: 'teacher', label: 'Teacher' },
    { value: 'PARENT', id: 'parent', label: 'Parent' },
    { value: 'OTHER', id: 'other', label: 'Other' },
  ];
  public projectName = environment.projectName
  constructor(
    private router: Router,
    private geocodingService: GeocodingService,
    private accountInfoService: AccountInfoService,
    private dataService: DataService,
    private translate: TranslateService,
    private modalService: NgbModal,
  ) {
  }

  ngOnInit() {
    this.initTypeahead();
    this.setTranslateLang();
    const browserLang = this.translate.currentLang;
    this.model.address.country = environment.projectName==='BAC'?'country_es':'country_be';
    this.dataService.getDictionary(browserLang)
      .pipe(
        map((i) => {
          let temp = [];
          const countryKeys = Object.keys(i).filter(key => key.includes('country_'));
          countryKeys.forEach(key => {
            temp.push({
              'value': key,
              'label': i[key]
            });
          })

          return temp;
        })
      )
      .subscribe(items => {
        this.countries = items.sort(compare);
      });

    const compare = (a, b) => {
      // Use toUpperCase() to ignore character casing
      const valueA = a.label.toUpperCase();
      const valueB = b.label.toUpperCase();

      let comparison = 0;
      if (valueA > valueB) {
        comparison = 1;
      } else if (valueA < valueB) {
        comparison = -1;
      }
      return comparison;
    };

    this.translate.onLangChange.subscribe((e) => {
      let temp = [];
      const countryKeys = Object.keys(e['translations']).filter(key => key.includes('country_'));
      countryKeys.forEach(key => {
        temp.push({
          'value': key,
          'label': e['translations'][key]
        });
      })
      this.countries = temp.sort(compare);
    });
  }

  setTranslateLang(){
    this.model.language=localStorage.getItem('user_lng');
  }

  getTranslations(translations, lang) {
    this.genders.forEach((element) => {
      if (element.id in translations) {
        element['label'] = translations[element.id];
      }
    });

    this.iam.forEach((element) => {

      if (element.id in translations) {
        element['label'] = translations[element.id].charAt(0).toUpperCase() + translations[element.id].slice(1);
      }
    })
  }

  initTypeahead() {

    if (this.translate.translations && this.translate.translations[this.translate.currentLang]) {
      this.getTranslations(this.translate.translations[this.translate.currentLang], this.translate.currentLang);
    }

    this.translate.onLangChange.subscribe((e) => {
      this.getTranslations(e['translations'], e['lang']);
    });

    this.address$ = concat(
      of([]),
      this.addressInput$.pipe(
        distinctUntilChanged(),
        tap(() => this.addressLoading = true),
        switchMap(term => this.geocodingService.addressAutocomplete(term, null, null).pipe(
          catchError(() => of([])),
          map(result => result.features.map(r => Object.assign(r.properties, r.geometry))),
          tap(() => this.addressLoading = false)
        ))
      )
    );
    const { data } = history.state;
    if (data) {
      const { attributes } = data;
      if (!_.isEmpty(attributes)) {
        this.accountInfoPageAgree = attributes.acceptedPrivacyPolicy && attributes.acceptedPrivacyPolicy[0];
        this.accountInfoPageRead = attributes.acceptedTermsOfUse && attributes.acceptedTermsOfUse[0];
        this.model.gender = attributes.gender && attributes.gender[0];
        this.model.address = attributes.address && attributes.address[0];
        this.model.firstName = attributes.firstName && attributes.firstName[0];
        this.model.iam = attributes.userType && attributes.userType[0];
        this.model.birthYear = attributes.birthYear && attributes.birthYear[0];
      }
    }
  }

  get lang(): string {
    return this.translate.currentLang;
  }

  set lang(value: string) {
    localStorage.setItem('user_lng', value);
    this.translate.use(value);
  }


  languageChange(selectedLang: string){
    this.model.language = selectedLang;
  }
  public updateInfo() {
    if (this.saving) {
      return;
    }
    this.model.acceptedTermsOfUse = this.accountInfoPageRead && this.accountInfoPageAgree;
    this.model.acceptedPrivacyPolicy = this.accountInfoPageRead && this.accountInfoPageAgree;
    this.model.userId = JSON.parse(localStorage.getItem('user_id'));
    const payload = {
      firstName: this.model.firstName.trim(),
      address: {
        street: this.model.address.street.trim(),
        houseNumber: this.model.address.houseNumber.trim(),
        postalCode: this.model.address.postalCode.trim(),
        municipality: this.model.address.municipality.trim(),
        country: this.model.address.country.trim(),
      },
      gender: this.model.gender.trim(),
      acceptedTermsOfUse: this.model.acceptedTermsOfUse,
      acceptedPrivacyPolicy: this.model.acceptedPrivacyPolicy,
      sendToInboxFromPhone: this.model.sendToInboxFromPhone,
      language: this.model.language.trim(),
      userId: this.model.userId,
      iam: this.model.iam.trim(),
      birthYear: this.model.birthYear || null,
    };
    this.saving = true;
    this.accountInfoService.update(payload).subscribe(() => {
      this.router.navigate(['/', 'help'], { state: { skipGuard: true } });
    },
      err => {
        this.saving = false;
        const infoDlg = this.modalService.open(ErrorModalComponent);
        infoDlg.componentInstance.message = err.error[0].message;
      });
  }

  isBirthYearValid() {
    return this.model.birthYear === null || (this.model.birthYear <= this.getMaxBirthYear() && this.model.birthYear >= this.getMinBirthYear());
  }

  getMaxBirthYear() {
    return new Date().getFullYear() - 10;
  }

  getMinBirthYear() {
    return new Date().getFullYear() - 100;
  }

  handleBirthInput(t, e) {
    this.isBirthYearValid() ? t.close() : t.open();

    const previousValue = e.target.defaultValue;
    const currentValue = e.target.valueAsNumber;

    if (isNaN(previousValue)) {
      e.target.defaultValue = this.getMaxBirthYear();
      this.model.birthYear = this.getMaxBirthYear();
    } else {
      e.target.defaultValue  = currentValue;
    }
  }

  public isComplete(): boolean {
    return !!(this.model.firstName && this.model.firstName.trim()
      && this.model.gender && this.model.gender.trim()
      && this.isBirthYearValid()
      && this.model.iam && this.model.iam.trim()
      && !_.isEmpty(this.model.address)
      && this.model.address.houseNumber && this.model.address.houseNumber.trim()
      && this.model.address.country && this.model.address.country.trim()
      && this.model.address.municipality && this.model.address.municipality.trim()
      && this.model.address.postalCode && this.model.address.postalCode.trim()
      && this.model.address.street && this.model.address.street.trim());
  }
}
